import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { getEmsOfficesById, getMccLowRisk } from "../../api";

const initialState = {
  applicationData: null,
  editSlug: null,
  mccOptions: [],
  emsOfficeOptions: [],
  step: 0,
  mobileStepperOpen: false,
  isAnonymousVerified: true,
  isOtpSent: false,
  isAuthenticated: false,
  otpCode: "",
  activeSigner: null,
  activeOtpMethod: "sms",
  countryList: [],
  otpVerificationTime: null,
  minimumData: null,
  isSignInPersonModalOpen: false,
  isFinicityConnected: false,
  hasFinicityRoutingAndAccountNumber: false,
  finictyConnectLoading: false,
  isPlaidConnected: false,
  hasPlaidRoutingAndAccountNumber: false,
  plaidConnectLoading: false,
  signerType: undefined,
  uploadedFiles: [],
  validatedPhoneNumbers: {},
  validatedDriverLicenses: {},
  validatedRoutingNumbers: {},
  validationStatuses: {},
};

const useSalesFormStore = create(
  devtools((set, get) => ({
    ...initialState,
    setApplicationData: (t) => set({ applicationData: t }),
    setEditSlug: (t) => set({ editSlug: t }),
    setMccOptions: (t) => set({ mccOptions: t }),
    setStep: (t) => set({ step: t }),
    toggleMobileStepperOpen: () =>
      set({ mobileStepperOpen: !get().mobileStepperOpen }),
    reset: () => set(initialState),
    setIsAnonymousVerified: (t) => set({ isAnonymousVerified: t }),
    setIsAuthenticated: (t) => set({ isAuthenticated: t }),
    setIsOtpSent: (t) => set({ isOtpSent: t }),
    setOtpCode: (t) => set({ otpCode: t }),
    setActiveSigner: (t) => set({ activeSigner: t }),
    setActiveOtpMethod: (t) => set({ activeOtpMethod: t }),
    setCountryList: (t) => set({ countryList: t }),
    setOtpVerificationTime: (t) => set({ otpVerificationTime: t }),
    setMinimumData: (t) => set({ minimumData: t }),
    setIsSignInPersonModalOpen: (t) => set({ isSignInPersonModalOpen: t }),
    setSignerType: (t) => set({ signerType: t }),
    setUploadedFiles: (t) => set({ uploadedFiles: t }),
    setIsFinicityConnected: (t) => set({ isFinicityConnected: t }),
    setFinictyConnectLoading: (t) => set({ finictyConnectLoading: t }),
    setHasFinicityRoutingAndAccountNumber: (t) =>
      set({ hasFinicityRoutingAndAccountNumber: t }),
    setIsPlaidConnected: (t) => set({ isPlaidConnected: t }),
    setPlaidConnectLoading: (t) => set({ plaidConnectLoading: t }),
    setHasPlaidRoutingAndAccountNumber: (t) =>
      set({ hasPlaidRoutingAndAccountNumber: t }),
    addValidatedPhoneNumber: (number, status) =>
      set((state) => ({
        validatedPhoneNumbers: {
          ...state.validatedPhoneNumbers,
          [number]: status,
        },
      })),
    clearValidatedPhoneNumbers: () => set({ validatedPhoneNumbers: {} }),
    getValidatedPhoneNumber: (t) => get().validatedPhoneNumbers[t],
    addValidatedDriverLicense: (license, licenseState, status) =>
      set((state) => ({
        validatedDriverLicenses: {
          ...state.validatedDriverLicenses,
          [`${license}-${licenseState}`]: status,
        },
      })),
    clearValidatedDriverLicenses: () => set({ validatedDriverLicenses: {} }),
    getValidatedDriverLicense: (license, state) =>
      get().validatedDriverLicenses[`${license}-${state}`],
    addValidatedRoutingNumber: (routingNumber, status, bankName) =>
      set((state) => ({
        validatedRoutingNumbers: {
          ...state.validatedRoutingNumbers,
          [routingNumber]: { status, bankName },
        },
      })),
    clearValidatedRoutingNumbers: () => set({ validatedRoutingNumbers: {} }),
    getValidatedRoutingNumber: (routingNumber) =>
      get().validatedRoutingNumbers[routingNumber],
    setValidationStatus: (fieldName, status) =>
      set({
        validationStatuses: {
          ...get().validationStatuses,
          [fieldName]: status,
        },
      }),
    clearValidationStatuses: () => set({ validationStatuses: {} }),
  })),
);

// Browsing without a search term only needs a first page. A search needs enough
// headroom that common terms are not cut off ("store" matches 180, "service" 324) —
// the dropdown renders the server list verbatim, so anything trimmed is unreachable.
const MCC_BROWSE_LIMIT = 100;
const MCC_SEARCH_LIMIT = 500;

let mccRequestSeq = 0;

export const fetchMccLowRisk = async (searchTerm = "", limit) => {
  const seq = ++mccRequestSeq;
  try {
    const res = await getMccLowRisk({
      search: searchTerm,
      limit: limit ?? (searchTerm ? MCC_SEARCH_LIMIT : MCC_BROWSE_LIMIT),
    });
    if (seq !== mccRequestSeq) return;

    if (!res?.success) {
      useSalesFormStore.setState({ mccOptions: [] });
      return;
    }

    const mccOptions = res?.data.map((item) => ({
      value: item.id,
      label: item.description,
    }));

    useSalesFormStore.setState({ mccOptions });
  } catch (error) {
    if (seq !== mccRequestSeq) return;
    useSalesFormStore.setState({ mccOptions: [] });
    console.error("Problem fetching MCC low risk data");
  }
};

export const fetchEMSOffices = async () => {
  try {
    const res = await getEmsOfficesById();
    if (res.status) {
      useSalesFormStore.setState({ emsOfficeOptions: res.data });
    }
  } catch (error) {
    console.error("Problem fetching EMS office list");
  }
};

export default useSalesFormStore;
